import { useMemo } from 'react';

import { URLS, createJurisdictionHeaders } from '@/data';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';
import { useUserStore } from '@/hooks/use-user-store';
import { useQuery } from '@tanstack/react-query';

import { SbkPromo } from '../types';

export const sbkBetslipPromoKeys = {
    all: ['sbk-betslip-promos'] as const,
    list: (userId: string, product: string) => [...sbkBetslipPromoKeys.all, userId, product] as const,
};

/**
 * Fetches the user's promotions for the sportsbook betslip.
 * GET /auth/user/promotions
 */
const getSbkBetslipPromos = async (): Promise<SbkPromo[]> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/promotions`, {
        method: 'GET',
        headers: createJurisdictionHeaders(),
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return data?.data ?? [];
};

/**
 * Fetches the user's promotions for the sportsbook betslip.
 * Uses a separate query key from useSegmentedPromos to avoid cache collisions.
 */
export const useSbkBetslipPromos = () => {
    const userId = useUserStore(state => state.profile.sub);
    const activeProduct = useJurisdictionStore(state => state.product);
    const activeCurrency = useSocialCurrencyStore(state => state.activeCurrency);
    const isPredictions = useIsPredictionsProduct();
    const isPromosEnabled = activeCurrency === SocialCurrency.CASH && !isPredictions;

    const { data, isSuccess } = useQuery<SbkPromo[]>({
        queryKey: sbkBetslipPromoKeys.list(userId, activeProduct),
        queryFn: getSbkBetslipPromos,
        enabled: isPromosEnabled,
    });

    const promos: SbkPromo[] = useMemo(() => {
        if (!isPromosEnabled) {
            return [];
        }
        return data ?? [];
    }, [data, isPromosEnabled]);

    return { promos, isSuccess: isPromosEnabled && isSuccess };
};
